/**
 * AppContext - Unified authentication context for the application.
 *
 * This context provides authentication that works with both Auth0 (production)
 * and local tokens (localhost development). It automatically detects the
 * environment and uses the appropriate authentication method.
 *
 * Usage:
 *   import { useAppAuth } from './contexts/AppContext';
 *   const { user, isAuthenticated, getIdTokenClaims } = useAppAuth();
 */

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useContext,
  ReactNode,
} from 'react';
import { useAuth0, User as Auth0User } from '@auth0/auth0-react';
import { clearChannelCache } from '@eptai/eptai-app-components';
import {
  getLocalToken,
  isTokenValid,
  parseUserFromToken,
  isTokenCloseToExpiry,
  decodeJwtSegment,
  LocalUser,
  TokenClaims,
} from '../utils/localAuth';
import { beginLogoutNavigation, isLeavingForLogout } from '../utils/appLogin';
import { clearEptSessionCookie } from '../utils/crossDomainAuth';
import {
  broadcastCognitoLogout,
  clearCognitoSession,
  cognitoLogoutUrl,
  CognitoTokens,
  isCognitoCallback,
  readCognitoSession,
  redirectToCognito,
  refreshCognitoSession,
  requestCognitoSessionFromOtherTabs,
  shareCognitoSessionWithNewTabs,
  storeCognitoSession,
} from '../utils/cognitoAuth';
import {
  apiPathV4,
  hasCognitoLane,
  storeEnvironmentForAuth,
  usesEmulatorAuth,
  isSalesforceEmbedded,
} from '../config';

// Types
interface AppUser {
  sub: string;
  email?: string;
  name?: string;
  nickname?: string;
  picture?: string;
  ept_roles?: string[];
  ept_app_metadata?: { account_id: number; [key: string]: unknown };
  userInfo?: Record<string, unknown>;
  [key: string]: unknown;
}

interface AppAuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: AppUser | null;
  getIdTokenClaims: () => Promise<TokenClaims | null>;
  getAccessTokenSilently: (options?: {
    cacheMode?: 'on' | 'off' | 'cache-only';
  }) => Promise<string | null>;
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void;
  /** Adopt tokens from a completed Cognito hosted-UI sign-in (EPT-3938). */
  adoptCognitoSession: (tokens: CognitoTokens) => void;
  loginWithRedirect: (options?: {
    authorizationParams?: {
      redirect_uri?: string;
      login_hint?: string;
      /** Skip Auth0 home-realm discovery and go straight to this connection (EPD-79). */
      connection?: string;
    };
    appState?: { returnTo?: string; [key: string]: unknown };
  }) => Promise<void>;
}

const AppAuthContext = createContext<AppAuthContextType | null>(null);
const CANVAS_ACCESS_TOKEN_STORAGE_KEY = 'salesforce_canvas_access_token';
const CANVAS_ID_TOKEN_STORAGE_KEY = 'salesforce_canvas_id_token';

/**
 * Hook to use authentication - works with both local and Auth0 auth.
 */
export function useAppAuth(): AppAuthContextType {
  const context = useContext(AppAuthContext);
  if (!context) {
    throw new Error('useAppAuth must be used within an AppAuthProvider');
  }
  return context;
}

interface AppAuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that handles both local and Auth0 authentication.
 * Must be used inside Auth0Provider when not in local mode.
 */
export function AppAuthProvider({ children }: AppAuthProviderProps): React.JSX.Element {
  const isLocal = usesEmulatorAuth();
  const isSalesforceCanvas = !isLocal && isSalesforceEmbedded();

  // Always call useAuth0 (it's required by Auth0Provider)
  // In local mode, Auth0Provider still wraps the app but we ignore its auth
  const auth0 = useAuth0();

  // Local auth state
  const [localToken, setLocalToken] = useState<string | null>(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [localAuthenticated, setLocalAuthenticated] = useState(false);
  const [localUser, setLocalUser] = useState<LocalUser | null>(null);
  const [canvasToken, setCanvasToken] = useState<string | null>(null);
  const [canvasIdToken, setCanvasIdToken] = useState<string | null>(null);
  const [canvasLoading, setCanvasLoading] = useState(isSalesforceCanvas);
  const [canvasUser, setCanvasUser] = useState<AppUser | null>(null);

  // Cognito enterprise SSO lane. Restored synchronously so a refresh does not
  // flash the login page before the session is recognised.
  const [cognitoTokens, setCognitoTokens] = useState<CognitoTokens | null>(() =>
    readCognitoSession()
  );
  const [cognitoUser, setCognitoUser] = useState<AppUser | null>(null);
  // A new tab has no sessionStorage; until the open tabs have had a chance to
  // hand theirs over, the user is not known to be signed out.
  const [cognitoRestoring, setCognitoRestoring] = useState(
    () =>
      !isLocal &&
      !isSalesforceCanvas &&
      hasCognitoLane() &&
      !readCognitoSession() &&
      !isCognitoCallback()
  );

  const decodeJwt = useCallback((token: string): TokenClaims | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return { __raw: token } as TokenClaims;
      }
      const payload = JSON.parse(decodeJwtSegment(parts[1])) as Record<string, unknown>;
      return { __raw: token, ...payload } as TokenClaims;
    } catch (error) {
      console.error('[AUTH] Error decoding JWT:', error);
      return null;
    }
  }, []);

  const tokenClaimsToUser = useCallback((claims: TokenClaims | null): AppUser | null => {
    if (!claims) return null;
    return {
      sub: String(claims.sub || ''),
      email: typeof claims.email === 'string' ? claims.email : undefined,
      name: typeof claims.name === 'string' ? claims.name : undefined,
      nickname: typeof claims.nickname === 'string' ? claims.nickname : undefined,
      picture: typeof claims.picture === 'string' ? claims.picture : undefined,
      ept_roles: Array.isArray(claims.ept_roles) ? (claims.ept_roles as string[]) : undefined,
      ept_app_metadata: claims.ept_app_metadata as AppUser['ept_app_metadata'],
      userInfo: claims.userInfo as Record<string, unknown> | undefined,
    };
  }, []);

  const adoptCognitoSession = useCallback(
    (tokens: CognitoTokens): void => {
      storeCognitoSession(tokens);
      setCognitoTokens(tokens);
      setCognitoUser(tokenClaimsToUser(decodeJwt(tokens.idToken)));
    },
    [decodeJwt, tokenClaimsToUser]
  );

  useEffect(() => {
    if (!isLocal && !isSalesforceCanvas && hasCognitoLane()) {
      // Another tab logged out: drop this tab's session too, as a reload would.
      shareCognitoSessionWithNewTabs(() => setCognitoTokens(null));
    }
  }, [isLocal, isSalesforceCanvas]);

  useEffect(() => {
    if (!cognitoRestoring) return;
    let cancelled = false;
    void requestCognitoSessionFromOtherTabs().then(tokens => {
      if (cancelled) return;
      if (tokens) {
        adoptCognitoSession(tokens);
      }
      setCognitoRestoring(false);
    });
    return () => {
      cancelled = true;
    };
  }, [cognitoRestoring, adoptCognitoSession]);

  // Keep the Cognito user in step with the stored id token, and swap an expired
  // one for a fresh pair rather than dropping the user to the login page.
  useEffect(() => {
    if (!cognitoTokens) {
      setCognitoUser(null);
      return;
    }
    if (isTokenValid(cognitoTokens.idToken)) {
      setCognitoUser(tokenClaimsToUser(decodeJwt(cognitoTokens.idToken)));
      return;
    }
    let cancelled = false;
    void refreshCognitoSession().then(refreshed => {
      // Mid-logout, a null here is not an expired session: leave state alone.
      if (cancelled || isLeavingForLogout()) return;
      if (refreshed) {
        setCognitoTokens(refreshed);
      } else {
        clearCognitoSession();
        setCognitoTokens(null);
        setCognitoUser(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [cognitoTokens, decodeJwt, tokenClaimsToUser]);

  const restoreCanvasSession = useCallback(
    (accessToken: string, idToken: string): boolean => {
      if (!isTokenValid(accessToken) || !isTokenValid(idToken)) {
        return false;
      }

      const claims = decodeJwt(idToken);
      if (!claims) {
        return false;
      }

      setCanvasToken(accessToken);
      setCanvasIdToken(idToken);
      setCanvasUser(tokenClaimsToUser(claims));
      return true;
    },
    [decodeJwt, tokenClaimsToUser]
  );

  // Fetch local token on mount and periodically when on localhost
  useEffect(() => {
    if (!isLocal) {
      setLocalLoading(false);
      return;
    }

    let mounted = true;

    // Check localStorage first for immediate auth state
    const checkLocalStorage = (): boolean => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && isTokenValid(storedToken)) {
        const user = parseUserFromToken(storedToken);
        if (user) {
          setLocalToken(storedToken);
          setLocalUser(user);
          setLocalAuthenticated(true);
          setLocalLoading(false);
          console.log('[LOCAL AUTH] Using token from localStorage, authenticated=true');
          return true;
        }
      }
      return false;
    };

    // Try localStorage first for immediate auth
    const hasValidToken = checkLocalStorage();

    // Only fetch from API if we don't have a valid token in localStorage
    if (!hasValidToken) {
      setLocalLoading(true);

      const fetchLocalToken = async (): Promise<void> => {
        const token = await getLocalToken();
        if (!mounted) return;

        if (token && isTokenValid(token)) {
          setLocalToken(token);
          localStorage.setItem('token', token);
          const user = parseUserFromToken(token);
          if (user) {
            setLocalUser(user);
            setLocalAuthenticated(true);
            console.log('[LOCAL AUTH] Set authenticated=true from API');
          }
        } else {
          console.warn('[LOCAL AUTH] Token invalid or missing, setting authenticated=false');
          setLocalAuthenticated(false);
          setLocalToken(null);
        }
        setLocalLoading(false);
      };

      fetchLocalToken();
    }

    // Refresh token periodically if close to expiring
    const checkAndRefreshToken = async (): Promise<void> => {
      if (!mounted) return;

      const storedToken = localStorage.getItem('token');
      if (!storedToken || !isTokenValid(storedToken) || isTokenCloseToExpiry(storedToken)) {
        const token = await getLocalToken();
        if (token && isTokenValid(token)) {
          setLocalToken(token);
          localStorage.setItem('token', token);
          const user = parseUserFromToken(token);
          if (user) {
            setLocalUser(user);
            setLocalAuthenticated(true);
          }
        }
      }
    };

    // Check token expiry every 5 minutes
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [isLocal]);

  useEffect(() => {
    if (!isSalesforceCanvas) return;

    const url = new URL(window.location.href);
    const canvasSessionId = url.searchParams.get('canvas_session_id');
    const accountId = url.searchParams.get('account_id');
    const hasCanvasBootstrapParams = Boolean(canvasSessionId && accountId);
    const storedAccessToken = sessionStorage.getItem(CANVAS_ACCESS_TOKEN_STORAGE_KEY);
    const storedIdToken = sessionStorage.getItem(CANVAS_ID_TOKEN_STORAGE_KEY);
    console.log(
      '[SALESFORCE CANVAS] Embedded mode detected',
      window.location.href,
      canvasSessionId,
      accountId
    );
    console.log('[SALESFORCE CANVAS][DEBUG] document.referrer', document.referrer);
    console.log('[SALESFORCE CANVAS][DEBUG] document.cookie', document.cookie);
    console.log(
      '[SALESFORCE CANVAS][DEBUG] query params',
      Object.fromEntries(url.searchParams.entries())
    );
    console.log(
      '[SALESFORCE CANVAS][DEBUG] session storage',
      Object.keys(sessionStorage).reduce<Record<string, string | null>>((acc, key) => {
        acc[key] = sessionStorage.getItem(key);
        return acc;
      }, {})
    );
    if (!hasCanvasBootstrapParams && !canvasToken && storedAccessToken && storedIdToken) {
      if (restoreCanvasSession(storedAccessToken, storedIdToken)) {
        setCanvasLoading(false);
        console.log('[SALESFORCE CANVAS] Restored session from sessionStorage');
        return;
      }

      sessionStorage.removeItem(CANVAS_ACCESS_TOKEN_STORAGE_KEY);
      sessionStorage.removeItem(CANVAS_ID_TOKEN_STORAGE_KEY);
    }

    if (!hasCanvasBootstrapParams && canvasToken) {
      setCanvasLoading(false);
      return;
    }

    if (!hasCanvasBootstrapParams) {
      setCanvasLoading(false);
      return;
    }

    let mounted = true;
    setCanvasLoading(true);

    const exchangeCanvasSession = async (): Promise<void> => {
      try {
        const requestBody = {
          account_id: Number(accountId),
          canvas_session_id: canvasSessionId,
        };
        console.log(
          '[SALESFORCE CANVAS] Starting session exchange',
          accountId,
          canvasSessionId,
          apiPathV4(),
          requestBody
        );
        const response = await fetch(`${apiPathV4()}/salesforce/canvas/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        const rawResponseText = await response.clone().text();
        console.log('[SALESFORCE CANVAS][DEBUG] session exchange raw response', {
          status: response.status,
          ok: response.ok,
          headers: Object.fromEntries(response.headers.entries()),
          body: rawResponseText,
        });

        if (!response.ok) {
          throw new Error(`Canvas session exchange failed: ${response.status}`);
        }

        const data = (await response.json()) as {
          token?: string;
          access_token?: string;
          id_token?: string;
        };
        const accessToken = data.access_token || data.token;
        const idToken = data.id_token || accessToken;
        if (!accessToken || !idToken) {
          throw new Error('Canvas session exchange did not return Auth0 tokens');
        }

        const claims = decodeJwt(idToken);
        if (!mounted) return;

        console.log('[SALESFORCE CANVAS][DEBUG] access token', accessToken);
        console.log('[SALESFORCE CANVAS][DEBUG] id token', idToken);
        console.log('[SALESFORCE CANVAS][DEBUG] decoded claims', claims);
        sessionStorage.setItem(CANVAS_ACCESS_TOKEN_STORAGE_KEY, accessToken);
        sessionStorage.setItem(CANVAS_ID_TOKEN_STORAGE_KEY, idToken);
        restoreCanvasSession(accessToken, idToken);

        url.searchParams.delete('canvas_session_id');
        url.searchParams.delete('account_id');
        window.history.replaceState({}, '', url.toString());
        console.log('[SALESFORCE CANVAS] Session exchange succeeded', claims, window.location.href);
      } catch (error) {
        console.error('[SALESFORCE CANVAS] Session exchange failed:', error);
        sessionStorage.removeItem(CANVAS_ACCESS_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(CANVAS_ID_TOKEN_STORAGE_KEY);
        if (mounted) {
          setCanvasToken(null);
          setCanvasIdToken(null);
          setCanvasUser(null);
        }
      } finally {
        if (mounted) {
          setCanvasLoading(false);
        }
      }
    };

    exchangeCanvasSession();

    return () => {
      mounted = false;
    };
  }, [isSalesforceCanvas, canvasToken, restoreCanvasSession]);

  // Build the auth context value based on local vs Auth0 mode
  const getIdTokenClaims = useCallback(async (): Promise<TokenClaims | null> => {
    if (cognitoTokens) {
      if (isLeavingForLogout()) return null;
      // The app sends the ID token: Cognito access tokens carry no ept_* claims.
      const claims = decodeJwt(cognitoTokens.idToken);
      if (claims) {
        return { ...claims, __raw: cognitoTokens.idToken, __id_raw: cognitoTokens.idToken };
      }
    }

    if (canvasIdToken && canvasToken) {
      const claims = decodeJwt(canvasIdToken);
      if (!claims) return null;
      return { ...claims, __raw: canvasToken, __id_raw: canvasIdToken };
    }

    if (isLocal) {
      // Local mode: return local token
      let tokenToUse = localToken;
      if (!localToken || !isTokenValid(localToken)) {
        try {
          const newToken = await getLocalToken();
          if (newToken) {
            setLocalToken(newToken);
            localStorage.setItem('token', newToken);
            tokenToUse = newToken;
          } else {
            return null;
          }
        } catch (error) {
          console.warn('[LOCAL AUTH] Failed to refresh token:', error);
          return null;
        }
      }

      if (!tokenToUse) return null;

      // Decode token to include claims
      try {
        return decodeJwt(tokenToUse);
      } catch (error) {
        console.error('[LOCAL AUTH] Error decoding token:', error);
        return tokenToUse ? { __raw: tokenToUse } : null;
      }
    } else {
      // Auth0 mode
      try {
        const claims = await auth0.getIdTokenClaims();
        if (!claims) return null;
        return claims as TokenClaims;
      } catch (error) {
        console.error('[AUTH0] Error getting token claims:', error);
        return null;
      }
    }
  }, [canvasIdToken, canvasToken, cognitoTokens, decodeJwt, isLocal, localToken, auth0]);

  const getAccessTokenSilently = useCallback(
    async (options?: { cacheMode?: 'on' | 'off' | 'cache-only' }): Promise<string | null> => {
      if (cognitoTokens) {
        // Logged out, even if the logout navigation never commits.
        if (isLeavingForLogout()) return null;
        // Callers send this to api.ept.ai, which reads the ept_* claims, so the
        // ID token is the one that carries an account. A stale one is refreshed
        // rather than returned: the API would reject it.
        if (isTokenValid(cognitoTokens.idToken)) {
          return cognitoTokens.idToken;
        }
        const refreshed = await refreshCognitoSession();
        if (refreshed) {
          setCognitoTokens(refreshed);
          return refreshed.idToken;
        }
        return null;
      }

      if (canvasToken) {
        return canvasToken;
      }

      if (isLocal) {
        // Local mode: return local token
        if (localToken && isTokenValid(localToken)) {
          return localToken;
        }
        const token = await getLocalToken();
        if (token && isTokenValid(token)) {
          setLocalToken(token);
          localStorage.setItem('token', token);
          return token;
        }
        return null;
      } else {
        // Auth0 mode — cacheMode: 'off' forces a network refresh for 401 recovery
        try {
          return await auth0.getAccessTokenSilently(options);
        } catch (error) {
          console.error('[AUTH0] Error getting access token:', error);
          return null;
        }
      }
    },
    [canvasToken, cognitoTokens, isLocal, localToken, auth0]
  );

  const logout = useCallback(
    (options?: { logoutParams?: { returnTo?: string } }): void => {
      clearChannelCache();
      // Must run before Auth0 navigates away — AuthStateMonitor may never
      // observe isAuthenticated=false on this page after logout redirect.
      clearEptSessionCookie();
      if (cognitoTokens) {
        // Also end the hosted-UI session, or the next sign-in silently reuses
        // it and never shows the IdP.
        //
        // Storage only, no setState: the page is unloading. Nulling the tokens
        // here flipped isAuthenticated before the navigation committed, which
        // made ForumListContext call redirectToAppLogin and replace the Cognito
        // logout with /login, so the hosted-UI session was never ended and the
        // realm hint signed the user straight back in. Auth0's SDK leaves React
        // state alone for the same reason.
        //
        // beginLogoutNavigation then keeps anything else (a 401 handler, a late
        // refresh) from redirecting to /login or restoring the session first.
        const returnTo = options?.logoutParams?.returnTo || window.location.origin;
        beginLogoutNavigation();
        broadcastCognitoLogout();
        clearCognitoSession();
        redirectToCognito(cognitoLogoutUrl(returnTo));
        return;
      }
      if (canvasToken) {
        sessionStorage.removeItem(CANVAS_ACCESS_TOKEN_STORAGE_KEY);
        sessionStorage.removeItem(CANVAS_ID_TOKEN_STORAGE_KEY);
        setCanvasToken(null);
        setCanvasIdToken(null);
        setCanvasUser(null);
      }
      if (isLocal) {
        setLocalToken(null);
        setLocalAuthenticated(false);
        setLocalUser(null);
        localStorage.removeItem('token');
        console.log('[LOCAL AUTH] Logged out');
      } else {
        auth0.logout(options);
      }
    },
    [canvasToken, cognitoTokens, isLocal, auth0]
  );

  const loginWithRedirect = useCallback(
    async (options?: {
      authorizationParams?: {
        redirect_uri?: string;
        login_hint?: string;
        connection?: string;
      };
      appState?: { returnTo?: string; [key: string]: unknown };
    }): Promise<void> => {
      if (isSalesforceCanvas) {
        console.warn('[SALESFORCE CANVAS] Ignoring Auth0 redirect in embedded mode');
        return;
      }
      if (isLocal) {
        // Local mode: try to get local token
        try {
          const token = await getLocalToken();
          if (token) {
            setLocalToken(token);
            localStorage.setItem('token', token);
            const user = parseUserFromToken(token);
            if (user) {
              setLocalUser(user);
              setLocalAuthenticated(true);
            }
          }
        } catch (error) {
          console.error('[LOCAL AUTH] Failed to get local token for login:', error);
        }
      } else {
        // Auth0 mode: store environment before redirect
        storeEnvironmentForAuth();
        await auth0.loginWithRedirect(options);
      }
    },
    [isLocal, isSalesforceCanvas, auth0]
  );

  const convertAuth0User = useCallback((auth0User: Auth0User | undefined): AppUser | null => {
    if (!auth0User) return null;
    const rawUser = auth0User as Record<string, unknown>;
    return {
      sub: auth0User.sub || '',
      email: auth0User.email,
      name: auth0User.name,
      nickname: auth0User.nickname,
      picture: auth0User.picture,
      ept_roles: rawUser.ept_roles as string[] | undefined,
      ept_app_metadata: rawUser.ept_app_metadata as AppUser['ept_app_metadata'],
      userInfo: rawUser.userInfo as Record<string, unknown> | undefined,
    };
  }, []);

  // Stable signature of the Auth0 user so downstream memos/effects only update
  // when the underlying user data changes, not on every render (auth0.user may
  // be a new object reference each render). This is the root fix for the
  // "Maximum update depth exceeded" re-render loop.
  const auth0User = auth0.user;
  const auth0UserKey = auth0User
    ? JSON.stringify([
        auth0User.sub,
        auth0User.email,
        auth0User.name,
        auth0User.nickname,
        auth0User.picture,
        (auth0User as Record<string, unknown>).ept_roles,
        (auth0User as Record<string, unknown>).ept_app_metadata,
        (auth0User as Record<string, unknown>).userInfo,
      ])
    : null;

  const convertedAuth0User = useMemo<AppUser | null>(
    () => convertAuth0User(auth0User),
    [auth0UserKey, convertAuth0User]
  );

  // A restored Cognito session is already resolved, so it must not wait on the
  // Auth0 SDK finishing its own silent-auth round trip.
  const isLoading = isLocal
    ? localLoading
    : cognitoTokens
      ? false
      : cognitoRestoring || canvasLoading || auth0.isLoading;
  const isAuthenticated = isLocal
    ? localAuthenticated
    : !!cognitoTokens || !!canvasToken || auth0.isAuthenticated;
  const user = useMemo<AppUser | null>(
    () => (isLocal ? localUser : cognitoUser || canvasUser || convertedAuth0User),
    [isLocal, localUser, cognitoUser, canvasUser, convertedAuth0User]
  );

  const contextValue = useMemo<AppAuthContextType>(
    () => ({
      isLoading,
      isAuthenticated,
      user,
      getIdTokenClaims,
      getAccessTokenSilently,
      logout,
      loginWithRedirect,
      adoptCognitoSession,
    }),
    [
      isLoading,
      isAuthenticated,
      user,
      getIdTokenClaims,
      getAccessTokenSilently,
      logout,
      loginWithRedirect,
      adoptCognitoSession,
    ]
  );

  return <AppAuthContext.Provider value={contextValue}>{children}</AppAuthContext.Provider>;
}

export default AppAuthContext;
